/**
 * Customer support (D-129), shared by the back office, the learner space (web) and the phone:
 * the deadline shown on a ticket, its tone, and the statuses a customer may act on.
 * The deadlines themselves are computed by the server (data: support_v1.json).
 */

export type SupportStatus = "new" | "open" | "waiting_customer" | "waiting_internal" | "resolved" | "closed";
export type SupportTone = "success" | "warning" | "danger" | "info" | "default";

export interface SupportTimer {
  target: string | null;
  due_at: string | null;
  seconds_left: number | null;
  paused: boolean;
}

export interface Remaining {
  /** "late" once the deadline is passed, "soon" within the warning window, else "ok". */
  state: "ok" | "soon" | "late" | "paused" | "none";
  hours: number;
  minutes: number;
  days: number;
}

/** Hours and minutes left (or late), with the state used by the chips and the alerts. */
export function remaining(timer: SupportTimer | null | undefined, soonHours = 4): Remaining {
  if (!timer || timer.seconds_left === null || timer.seconds_left === undefined) {
    return { state: "none", hours: 0, minutes: 0, days: 0 };
  }
  const left = timer.seconds_left;
  const abs = Math.abs(left);
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const days = Math.floor(hours / 24);
  if (timer.paused) return { state: "paused", hours, minutes, days };
  if (left < 0) return { state: "late", hours, minutes, days };
  return { state: left <= soonHours * 3600 ? "soon" : "ok", hours, minutes, days };
}

/** "3 h 05", "2 j 4 h" (FR) / "3 h 05", "2 d 4 h" (EN). */
export function durationText(value: Remaining, language: string): string {
  const day = language.startsWith("en") ? "d" : "j";
  if (value.days >= 1) return `${value.days} ${day} ${value.hours % 24} h`;
  return `${value.hours} h ${String(value.minutes).padStart(2, "0")}`;
}

export function timerTone(value: Remaining): SupportTone {
  if (value.state === "late") return "danger";
  if (value.state === "soon") return "warning";
  if (value.state === "paused") return "info";
  return "default";
}

export function statusTone(status: string): SupportTone {
  if (status === "resolved" || status === "closed") return "success";
  if (status === "waiting_customer") return "info";
  if (status === "new") return "warning";
  return "default";
}

export function priorityTone(priority: string): SupportTone {
  if (priority === "urgent") return "danger";
  if (priority === "high") return "warning";
  return "default";
}

/** A request the customer may still write on (a closed one asks for a new request). */
export function canReply(status: string): boolean {
  return status !== "closed";
}

/** Files accepted before the upload (the server checks again: type, size, real content). */
export function checkFile(file: { name: string; size: number; type: string }, rules: { max_bytes?: number; types?: string[] }): "ok" | "type" | "size" {
  const types = rules.types ?? [];
  if (types.length && !types.includes(file.type)) return "type";
  if (rules.max_bytes && file.size > rules.max_bytes) return "size";
  return "ok";
}
